<script>
import { mapGetters } from 'vuex';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import ChatList from '../../../components/ChatList.vue';
import ConversationBox from '../../../components/widgets/conversation/ConversationBox.vue';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import CmdBarConversationSnooze from 'dashboard/routes/dashboard/commands/CmdBarConversationSnooze.vue';
import { emitter } from 'shared/helpers/mitt';
import SidepanelSwitch from 'dashboard/components-next/Conversation/SidepanelSwitch.vue';
import ConversationSidebar from 'dashboard/components/widgets/conversation/ConversationSidebar.vue';
import BrandRail from 'dashboard/components/activeGamesHub/BrandRail.vue';
import EmailMailboxNav from 'dashboard/components/activeGamesHub/EmailMailboxNav.vue';
import {
  ACTIVE_GAMES_HUB_FEATURES,
  getActiveGamesHubEmailMailbox,
  getActiveGamesHubEmailTriage,
  getActiveGamesHubBrandFromQuery,
  isActiveGamesHubEmailScope,
} from 'dashboard/config/activeGamesHub';

const EMPTY_BRAND_INBOX_IDS = Object.freeze([]);

export default {
  components: {
    ChatList,
    ConversationBox,
    CmdBarConversationSnooze,
    SidepanelSwitch,
    ConversationSidebar,
    BrandRail,
    EmailMailboxNav,
  },
  beforeRouteLeave(to, from, next) {
    // Clear selected state if navigating away from a conversation to a route without a conversationId to prevent stale data issues
    // and resolves timing issues during navigation with conversation view and other screens
    if (this.conversationId) {
      this.$store.dispatch('clearSelectedState');
    }
    next(); // Continue with navigation
  },
  props: {
    inboxId: {
      type: [String, Number],
      default: 0,
    },
    conversationId: {
      type: [String, Number],
      default: 0,
    },
    label: {
      type: String,
      default: '',
    },
    teamId: {
      type: String,
      default: '',
    },
    conversationType: {
      type: String,
      default: '',
    },
    foldersId: {
      type: [String, Number],
      default: 0,
    },
  },
  setup() {
    const { uiSettings, updateUISettings } = useUISettings();
    const { accountId } = useAccount();

    return {
      uiSettings,
      updateUISettings,
      accountId,
    };
  },
  data() {
    return {
      showSearchModal: false,
    };
  },
  computed: {
    ...mapGetters({
      chatList: 'getAllConversations',
      currentChat: 'getSelectedChat',
    }),
    showConversationList() {
      return this.isOnExpandedLayout ? !this.conversationId : true;
    },
    showMessageView() {
      return this.conversationId ? true : !this.isOnExpandedLayout;
    },
    isOnExpandedLayout() {
      const {
        LAYOUT_TYPES: { CONDENSED },
      } = wootConstants;
      const { conversation_display_type: conversationDisplayType = CONDENSED } =
        this.uiSettings;
      return conversationDisplayType !== CONDENSED;
    },

    shouldShowSidebar() {
      if (!this.currentChat.id) {
        return false;
      }

      const { is_contact_sidebar_open: isContactSidebarOpen } = this.uiSettings;
      return isContactSidebarOpen;
    },
    activeHubBrand() {
      return getActiveGamesHubBrandFromQuery(this.$route.query.brand);
    },
    isActiveHubEmailScope() {
      return isActiveGamesHubEmailScope(this.$route.query.channel);
    },
    isActiveGamesHubEnabled() {
      return ACTIVE_GAMES_HUB_FEATURES.enabled;
    },
    activeHubInboxIds() {
      if (this.isActiveHubEmailScope) {
        return this.activeEmailMailbox.inboxIds;
      }
      return this.activeHubBrand?.inboxIds || EMPTY_BRAND_INBOX_IDS;
    },
    activeEmailMailbox() {
      return getActiveGamesHubEmailMailbox(this.$route.query.mailbox);
    },
    activeEmailTriage() {
      return getActiveGamesHubEmailTriage(this.$route.query.triage);
    },
    activeHubLabel() {
      return this.isActiveHubEmailScope ? this.activeEmailTriage.label : '';
    },
    activeHubScopeName() {
      if (this.isActiveHubEmailScope) {
        const mailboxNames = {
          all: this.$t('ACTIVE_GAMES_HUB.EMAIL_NAV.ALL'),
          info: this.$t('ACTIVE_GAMES_HUB.EMAIL_NAV.INFO'),
          events: this.$t('ACTIVE_GAMES_HUB.EMAIL_NAV.EVENTS'),
          marketing: this.$t('ACTIVE_GAMES_HUB.EMAIL_NAV.MARKETING'),
          operations: this.$t('ACTIVE_GAMES_HUB.EMAIL_NAV.OPERATIONS'),
          finance: this.$t('ACTIVE_GAMES_HUB.EMAIL_NAV.FINANCE'),
        };
        const triageNames = {
          needs_reply: this.$t('ACTIVE_GAMES_HUB.EMAIL_TRIAGE.NEEDS_REPLY'),
          notifications: this.$t('ACTIVE_GAMES_HUB.EMAIL_TRIAGE.NOTIFICATIONS'),
          review: this.$t('ACTIVE_GAMES_HUB.EMAIL_TRIAGE.REVIEW'),
          all: this.$t('ACTIVE_GAMES_HUB.EMAIL_TRIAGE.ALL'),
        };
        return `${mailboxNames[this.activeEmailMailbox.id]} · ${
          triageNames[this.activeEmailTriage.id]
        }`;
      }
      return this.activeHubBrand?.name || '';
    },
    defaultAssigneeTab() {
      return this.isActiveHubEmailScope
        ? wootConstants.ASSIGNEE_TYPE.ALL
        : wootConstants.ASSIGNEE_TYPE.ME;
    },
  },
  watch: {
    conversationId() {
      this.fetchConversationIfUnavailable();
    },
  },

  created() {
    // Clear selected state early if no conversation is selected
    // This prevents child components from accessing stale data
    // and resolves timing issues during navigation
    // with conversation view and other screens
    if (!this.conversationId) {
      this.$store.dispatch('clearSelectedState');
    }
  },

  mounted() {
    this.$store.dispatch('agents/get');
    this.$store.dispatch('portals/index');
    this.initialize();
    this.$watch('$store.state.route', () => this.initialize());
    this.$watch('chatList.length', () => {
      this.setActiveChat();
    });
  },

  methods: {
    onConversationLoad() {
      this.fetchConversationIfUnavailable();
    },
    initialize() {
      this.$store.dispatch('setActiveInbox', this.inboxId);
      this.setActiveChat();
    },
    toggleConversationLayout() {
      const { LAYOUT_TYPES } = wootConstants;
      const {
        conversation_display_type:
          conversationDisplayType = LAYOUT_TYPES.CONDENSED,
      } = this.uiSettings;
      const newViewType =
        conversationDisplayType === LAYOUT_TYPES.CONDENSED
          ? LAYOUT_TYPES.EXPANDED
          : LAYOUT_TYPES.CONDENSED;
      this.updateUISettings({
        conversation_display_type: newViewType,
        previously_used_conversation_display_type: newViewType,
      });
    },
    fetchConversationIfUnavailable() {
      if (!this.conversationId) {
        return;
      }
      const chat = this.findConversation();
      if (!chat) {
        this.$store.dispatch('getConversation', this.conversationId);
      }
    },
    findConversation() {
      const conversationId = parseInt(this.conversationId, 10);
      const [chat] = this.chatList.filter(c => c.id === conversationId);
      return chat;
    },
    setActiveChat() {
      if (this.conversationId) {
        const selectedConversation = this.findConversation();
        // If conversation doesn't exist or selected conversation is same as the active
        // conversation, don't set active conversation.
        if (
          !selectedConversation ||
          selectedConversation.id === this.currentChat.id
        ) {
          return;
        }
        const { messageId } = this.$route.query;
        this.$store
          .dispatch('setActiveChat', {
            data: selectedConversation,
            after: messageId,
          })
          .then(() => {
            emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId });
          });
      } else {
        this.$store.dispatch('clearSelectedState');
      }
    },
    onSearch() {
      this.showSearchModal = true;
    },
    closeSearch() {
      this.showSearchModal = false;
    },
  },
};
</script>

<template>
  <section class="flex w-full h-full min-w-0">
    <BrandRail v-if="isActiveGamesHubEnabled" />
    <ChatList
      :show-conversation-list="showConversationList"
      :conversation-inbox="inboxId"
      :label="label"
      :team-id="teamId"
      :conversation-type="conversationType"
      :folders-id="foldersId"
      :is-on-expanded-layout="isOnExpandedLayout"
      :brand-inbox-ids="activeHubInboxIds"
      :brand-name="activeHubScopeName"
      :brand-label="activeHubLabel"
      :default-assignee-tab="defaultAssigneeTab"
      @conversation-load="onConversationLoad"
    >
      <template v-if="isActiveHubEmailScope" #scope-navigation>
        <EmailMailboxNav />
      </template>
    </ChatList>
    <ConversationBox
      v-if="showMessageView"
      :inbox-id="inboxId"
      :is-on-expanded-layout="isOnExpandedLayout"
    >
      <SidepanelSwitch v-if="currentChat.id" />
    </ConversationBox>
    <ConversationSidebar v-if="shouldShowSidebar" :current-chat="currentChat" />
    <CmdBarConversationSnooze />
  </section>
</template>
